import { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import useIncidencias from '../../hooks/useIncidencias';
import useConfiguracion from '../../hooks/useConfiguracion';
import ComentariosPunto from '../Shell/ComentariosPunto';

const ESTADOS = {
  pendiente: { texto: 'Pendiente', color: '#B8730C', fondo: '#FDF3E3' },
  en_progreso: { texto: 'En proceso', color: '#2B6CB0', fondo: '#E9F1FA' },
  culminado: { texto: 'Culminado', color: '#1F7A4D', fondo: '#E7F2EC' },
  anulado: { texto: 'Anulado', color: '#9A9A96', fondo: '#EEEEEC' },
};

// Semáforo de avance — pendiente/en_progreso/culminado son una progresión
// lineal real (3 pasos), anulado es un estado terminal aparte que no encaja
// en esa barra (no "avanzó menos" que culminado, simplemente no aplica).
const PASOS_AVANCE = ['pendiente', 'en_progreso', 'culminado'];

const SEVERIDADES = {
  baja: { texto: 'Baja', color: '#145C39', fondo: '#E7F2EC' },
  media: { texto: 'Media', color: '#8A5608', fondo: '#FDF3E3' },
  alta: { texto: 'Alta', color: '#B03A11', fondo: '#FBE7E1' },
  critica: { texto: 'Crítica', color: '#A32020', fondo: '#FBE2E2' },
};

/**
 * Consulta pública por código de ticket (README diseño, secciones 5.10 y
 * 5.11). Usa `buscarIncidenciaPorTicket` (ya existía en `useIncidencias.js`)
 * contra el endpoint corregido en esta fase — nunca trae datos personales
 * del denunciante, ni siquiera al propio autor.
 */
const SeguimientoTicket = ({ onCerrar, codigoInicial = '' }) => {
  const [codigo, setCodigo] = useState(codigoInicial);
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState('');
  const [escaneando, setEscaneando] = useState(false);
  const [leyendoArchivo, setLeyendoArchivo] = useState(false);
  const inputQrRef = useRef(null);
  const { loading, buscarIncidenciaPorTicket } = useIncidencias();

  // Si llega con un código ya conocido (ej. "Ver mi ticket" justo tras
  // enviar el reporte), buscarlo de una vez en vez de obligar a tipearlo.
  useEffect(() => {
    if (codigoInicial) buscar(codigoInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoInicial]);

  const buscar = async (codigoBuscado) => {
    setError('');
    const resultado = await buscarIncidenciaPorTicket(codigoBuscado || codigo);
    if (resultado.success) {
      setTicket(resultado.data);
    } else {
      setError('No encontramos ningún reporte con ese código. Verifica que esté bien escrito.');
    }
  };

  // Alternativa a escanear con la cámara: si el ciudadano guardó el QR como
  // imagen (captura de pantalla, descarga), no puede apuntar su propia
  // cámara a su propia pantalla — acá se lee el QR directo del archivo con
  // jsQR, igual que el escaneo en vivo pero sobre una imagen fija.
  const manejarArchivoQR = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = ''; // permite volver a elegir el mismo archivo si falla
    if (!archivo) return;

    setError('');
    setLeyendoArchivo(true);
    const lector = new FileReader();
    lector.onload = () => {
      const imagen = new Image();
      imagen.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = imagen.width;
        canvas.height = imagen.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imagen, 0, 0);
        const datos = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const detectado = jsQR(datos.data, datos.width, datos.height);
        setLeyendoArchivo(false);
        if (detectado?.data) {
          setCodigo(detectado.data);
          buscar(detectado.data);
        } else {
          setError('No pudimos leer un código QR en esa imagen. Prueba con otra o ingresa el código a mano.');
        }
      };
      imagen.onerror = () => {
        setLeyendoArchivo(false);
        setError('No pudimos abrir esa imagen.');
      };
      imagen.src = lector.result;
    };
    lector.readAsDataURL(archivo);
  };

  if (escaneando) {
    return (
      <EscanerQR
        onDetectado={(valor) => { setEscaneando(false); setCodigo(valor); buscar(valor); }}
        onCancelar={() => setEscaneando(false)}
      />
    );
  }

  if (ticket) {
    return <TicketDetalle ticket={ticket} onVolver={() => setTicket(null)} onCerrar={onCerrar} />;
  }

  return (
    <div className="rw-overlay">
      <div className="rw-sheet">
        <div className="rw-step-header">
          <button type="button" className="rw-icon-btn" onClick={onCerrar} aria-label="Cerrar">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="rw-paso">
          <h2 className="rw-titulo">Seguimiento</h2>
          <p className="rw-subtitulo">Ingresa tu código de ticket para ver el estado de tu reporte.</p>

          <input
            type="text"
            className="st-input-codigo"
            placeholder="INC-AAAAMMDD-XXXXXXXX"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
          />
          <button type="button" className="rw-boton-primario" disabled={!codigo || loading} onClick={() => buscar()}>
            {loading ? 'Buscando…' : 'Ver mi ticket'}
          </button>

          {error && <p className="rw-error">{error}</p>}

          <div className="st-separador"><span>o</span></div>

          <div className="st-opciones-qr">
            <button type="button" className="st-tarjeta-qr" onClick={() => setEscaneando(true)}>
              <i className="fas fa-qrcode"></i>
              <span>Escanear con la cámara</span>
            </button>
            <button type="button" className="st-tarjeta-qr" onClick={() => inputQrRef.current?.click()} disabled={leyendoArchivo}>
              <i className={`fas ${leyendoArchivo ? 'fa-spinner fa-spin' : 'fa-image'}`}></i>
              <span>Subir imagen de tu QR</span>
            </button>
            <input ref={inputQrRef} type="file" accept="image/*" onChange={manejarArchivoQR} hidden />
          </div>

          <p className="rw-texto-ayuda st-aviso">
            ¿Guardaste el QR como foto? No hace falta apuntar la cámara a tu propia pantalla —
            usa "Subir imagen de tu QR" y lo leemos directo del archivo.
          </p>
          <p className="rw-texto-ayuda st-aviso">
            El código no es recuperable si lo pierdes — el reporte no está ligado a ninguna
            cuenta.
          </p>
        </div>
      </div>
    </div>
  );
};

/** Escaneo de QR con la cámara, usando jsQR sobre frames de un <video>. */
const EscanerQR = ({ onDetectado, onCancelar }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(document.createElement('canvas'));
  const rafRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let stream;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
          rafRef.current = requestAnimationFrame(tick);
        }
      })
      .catch(() => setError('No se pudo acceder a la cámara para escanear.'));

    const tick = () => {
      const video = videoRef.current;
      if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const codigo = jsQR(imageData.data, imageData.width, imageData.height);
        if (codigo?.data) {
          onDetectado(codigo.data);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    return () => {
      cancelAnimationFrame(rafRef.current);
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cc-pantalla">
      <video ref={videoRef} autoPlay playsInline muted className="cc-video" />
      <div className="st-visor-qr"></div>
      {error && <div className="cc-error">{error}</div>}
      <div className="cc-controles-inferiores">
        <button type="button" className="cc-btn-cancelar" onClick={onCancelar}>
          <i className="fas fa-times"></i>
        </button>
      </div>
    </div>
  );
};

/** Íconos por tipo de archivo para el carrusel y las miniaturas. */
const ICONO_EVIDENCIA = { Video: 'fa-video', Audio: 'fa-microphone', Documento: 'fa-file-pdf' };

const TABS = [
  { id: 'archivos', etiqueta: 'Mis archivos', icono: 'fa-photo-film' },
  { id: 'respuesta', etiqueta: 'Respuesta', icono: 'fa-comment-dots' },
  { id: 'historial', etiqueta: 'Historial', icono: 'fa-clock-rotate-left' },
  { id: 'comentarios', etiqueta: 'Comentarios', icono: 'fa-comments' },
];

/** Tarjeta de ticket público: semáforo de avance y severidad siempre
 * visibles en la cabecera, y el resto (evidencia/respuesta/historial) por
 * pestañas (HU-008 Fase 6) — antes todo iba en una sola columna larga con
 * scroll, difícil de encontrar algo puntual; ahora cada sección es su
 * propia pantalla, estilo apps de streaming. Nunca datos personales. */
const TicketDetalle = ({ ticket, onVolver, onCerrar }) => {
  const estado = ESTADOS[ticket.estado] || ESTADOS.pendiente;
  const severidad = SEVERIDADES[ticket.severidad];
  const pasoActual = PASOS_AVANCE.indexOf(ticket.estado);
  const esAnulado = ticket.estado === 'anulado';
  const [tabActiva, setTabActiva] = useState('archivos');
  const { configuracion } = useConfiguracion();

  return (
    <div className="rw-overlay">
      <div className="rw-sheet">
        <div className="rw-step-header">
          <button type="button" className="rw-icon-btn" onClick={onVolver} aria-label="Volver">
            <i className="fas fa-arrow-left"></i>
          </button>
          <button type="button" className="rw-icon-btn" onClick={onCerrar} aria-label="Cerrar" style={{ marginLeft: 'auto' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="rw-paso">
          <div className="st-ticket-cabecera">
            <span className="st-ticket-codigo">{ticket.inc_chTiket}</span>
            {severidad && (
              <span className="st-pildora" style={{ color: severidad.color, background: severidad.fondo }}>
                Severidad {severidad.texto}
              </span>
            )}
          </div>
          <h2 className="rw-titulo" style={{ fontSize: 19 }}>{ticket.categoria || 'Sin categoría'}</h2>
          <p className="rw-subtitulo">
            {ticket.direccion} · {ticket.inc_dtFechaIncidente} {ticket.inc_tmHoraIncidente}
          </p>

          {/* Semáforo de avance (README: estado = avance de la atención,
              independiente de la severidad) — antes solo había una píldora
              de texto, sin noción visual de cuánto falta. */}
          <div className="st-semaforo">
            {esAnulado ? (
              <div className="st-semaforo-anulado">
                <i className="fas fa-ban"></i> Este reporte fue anulado
              </div>
            ) : (
              <>
                <div className="st-semaforo-barra">
                  {PASOS_AVANCE.map((paso, i) => (
                    <span
                      key={paso}
                      className={`st-semaforo-segmento ${i <= pasoActual ? 'st-semaforo-activo' : ''}`}
                      style={i <= pasoActual ? { background: ESTADOS[paso].color } : undefined}
                    />
                  ))}
                </div>
                <div className="st-semaforo-etiquetas">
                  {PASOS_AVANCE.map((paso, i) => (
                    <span key={paso} className={i <= pasoActual ? 'st-semaforo-etiqueta-activa' : ''}>
                      {ESTADOS[paso].texto}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="st-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`st-tab ${tabActiva === tab.id ? 'st-tab-activa' : ''}`}
                onClick={() => setTabActiva(tab.id)}
              >
                <i className={`fas ${tab.icono}`}></i>
                {tab.etiqueta}
                {tab.id === 'archivos' && ticket.evidencia.length > 0 && (
                  <span className="st-tab-contador">{ticket.evidencia.length}</span>
                )}
              </button>
            ))}
          </div>

          {tabActiva === 'archivos' && (
            ticket.evidencia.length > 0 ? (
              <CarruselEvidencia evidencia={ticket.evidencia} />
            ) : (
              <p className="rw-texto-ayuda st-tab-vacio">
                No adjuntaste evidencia con este reporte.
              </p>
            )
          )}

          {tabActiva === 'respuesta' && (
            ticket.respuesta_entidad ? (
              <div className="st-respuesta-tarjeta">
                <p>{ticket.respuesta_entidad}</p>
              </div>
            ) : (
              <p className="rw-texto-ayuda st-tab-vacio">
                Todavía no hay una respuesta de la entidad para este reporte.
              </p>
            )
          )}

          {tabActiva === 'historial' && (
            <div className="st-timeline">
              {ticket.historial.map((h, i) => (
                <div key={i} className="st-timeline-item">
                  <span className="st-timeline-punto" style={{ background: estado.color }}></span>
                  <div>
                    <strong>{h.accion_display}</strong>
                    <p>{h.his_txDescripcion}</p>
                    <span className="st-timeline-fecha">
                      {new Date(h.his_dtFechaAccion).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tabActiva === 'comentarios' && (
            <ComentariosPunto incidenciaId={ticket.inc_inId} configuracion={configuracion} />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Carrusel de evidencia estilo "visor de historias" — una diapositiva a la
 * vez (swipe o flechas), contador y puntos, con botón de pantalla completa
 * real (Fullscreen API) en vez de la grilla de miniaturas + lightbox
 * separado que había antes.
 */
const CarruselEvidencia = ({ evidencia }) => {
  const [indice, setIndice] = useState(0);
  const contenidoRef = useRef(null);
  const inicioToqueX = useRef(null);

  const actual = evidencia[indice];
  const total = evidencia.length;

  const ir = (delta) => setIndice((i) => Math.max(0, Math.min(total - 1, i + delta)));

  const alTocarInicio = (e) => { inicioToqueX.current = e.touches[0].clientX; };
  const alTocarFin = (e) => {
    if (inicioToqueX.current == null) return;
    const delta = e.changedTouches[0].clientX - inicioToqueX.current;
    if (Math.abs(delta) > 40) ir(delta < 0 ? 1 : -1);
    inicioToqueX.current = null;
  };

  const alternarPantallaCompleta = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      contenidoRef.current?.requestFullscreen?.();
    }
  };

  return (
    <div className="st-carrusel">
      <div
        className="st-carrusel-diapositiva"
        ref={contenidoRef}
        onTouchStart={alTocarInicio}
        onTouchEnd={alTocarFin}
      >
        {actual.arc_chTipoArchivo === 'Imagen' && <img src={actual.arc_fileArchivo} alt={actual.arc_chNombre} />}
        {actual.arc_chTipoArchivo === 'Video' && <video src={actual.url_descarga} controls playsInline />}
        {actual.arc_chTipoArchivo === 'Audio' && (
          <div className="st-carrusel-audio">
            <i className="fas fa-microphone pe-icono pe-icono-audio"></i>
            <p>Nota de audio</p>
            <audio src={actual.url_descarga} controls style={{ width: '100%' }} />
          </div>
        )}
        {actual.arc_chTipoArchivo === 'Documento' && (
          <iframe src={actual.url_descarga} title={actual.arc_chNombre} className="st-carrusel-pdf" />
        )}
        {!['Imagen', 'Video', 'Audio', 'Documento'].includes(actual.arc_chTipoArchivo) && (
          <div className="st-carrusel-archivo">
            <i className="fas fa-file pe-icono pe-icono-archivo"></i>
            <p>{actual.arc_chNombre}</p>
            <a href={actual.url_descarga} target="_blank" rel="noreferrer" className="rw-boton-primario" style={{ display: 'inline-block' }}>
              Descargar
            </a>
          </div>
        )}

        <button type="button" className="st-carrusel-fullscreen" onClick={alternarPantallaCompleta} title="Pantalla completa">
          <i className="fas fa-expand"></i>
        </button>

        {total > 1 && (
          <>
            <button type="button" className="st-carrusel-flecha st-carrusel-flecha-izq" onClick={() => ir(-1)} disabled={indice === 0} aria-label="Anterior">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button type="button" className="st-carrusel-flecha st-carrusel-flecha-der" onClick={() => ir(1)} disabled={indice === total - 1} aria-label="Siguiente">
              <i className="fas fa-chevron-right"></i>
            </button>
            <span className="st-carrusel-contador">{indice + 1}/{total}</span>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="st-carrusel-puntos">
          {evidencia.map((ev, i) => (
            <button
              key={ev.arc_inId}
              type="button"
              className={`st-carrusel-punto ${i === indice ? 'st-carrusel-punto-activo' : ''}`}
              onClick={() => setIndice(i)}
              aria-label={`Ver adjunto ${i + 1}`}
            >
              {ev.arc_chTipoArchivo === 'Imagen'
                ? <img src={ev.arc_fileArchivo} alt="" />
                : <i className={`fas ${ICONO_EVIDENCIA[ev.arc_chTipoArchivo] || 'fa-file'}`}></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeguimientoTicket;
